














import { handleError } from "@/lib/utility/handleError";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { ICompany } from "@/models/company.entity";
import { ITag } from "@/models/tag.entity";
import { ActivityLog } from "@/models/activity-log.entity";
import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { mixins } from "vue-class-component";
import { Component, Prop } from "vue-property-decorator";
import SelectTags from "./v2/SelectTags.vue";

type IHasPartnerId = Pick<ICompany, "partnerId">;
type IHasTags = Pick<ICompany, "tags">;
type IHasTagIds = Pick<ICompany, "tagIds">;
type IHasId = Pick<ICompany, "id">;
type HasUpdateTags<T> = { updatePartial: (hasTagIds: IHasTagIds) => Promise<T> };

@Component({
  components: {
    SelectTags
  }
})
export default class SelectUpdateTags<
  T extends IHasPartnerId & IHasTags & IHasTagIds & IHasId & HasUpdateTags<T>
> extends mixins(PermissionMixin) {
  @Prop({ required: true })
  value!: T;

  @Prop({ required: true })
  resource!: BackendResourceEnum;

  @Prop({ default: false, required: false })
  disabled!: boolean;

  @Prop({ default: false, required: false })
  readonly!: boolean;

  loading = false;

  async onTagsChanged(tags: ITag[]) {
    this.loading = true;
    try {
      const previousTagIds = [...this.value.tagIds];
      const newTagIds = tags.map(tag => tag.id);

      this.value.tagIds = newTagIds;
      await this.value.updatePartial({ tagIds: this.value.tagIds });

      // Create activity logs for added and removed tags
      await this.createTagActivities(previousTagIds, newTagIds);
    } catch (error) {
      handleError(error);
    } finally {
      this.loading = false;
    }
  }

  private async createTagActivities(previousTagIds: string[], newTagIds: string[]) {
    const addedTags = newTagIds.filter(id => !previousTagIds.includes(id));
    const removedTags = previousTagIds.filter(id => !newTagIds.includes(id));

    if (removedTags.length > 0) {
      await this.createDeleteTagActivity(removedTags);
    }

    if (addedTags.length > 0) {
      await this.createAddTagActivity(addedTags);
    }
  }

  async createAddTagActivity(newTags: string[]) {
    await this.createTagActivity(ActivityTypeEnum.CREATE_TAG, newTags);
  }

  async createDeleteTagActivity(newTags: string[]) {
    await this.createTagActivity(ActivityTypeEnum.DELETE_TAG, newTags);
  }

  private async createTagActivity(
    activityType: ActivityTypeEnum.CREATE_TAG | ActivityTypeEnum.DELETE_TAG,
    newTags?: string[]
  ) {
    if (!newTags?.length) return;

    const source = {
      refType: this.resource,
      refId: this.value.id
    };
    const partnerId = this.value.partnerId;

    await new ActivityLog().createTagActivity({
      partnerId,
      source,
      tagIds: newTags,
      activityType
    });
  }
}
